import { UseQueryResult } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { parseRequestError } from '../../utilities'
import AppAlertComponent from '../AppAlertComponent/AppAlertComponent'
import LoadingCard from '../LoadingCard'

// This type maps each query result to its data type while preserving the tuple structure
type ExtractDataFromQueries<
  TQueries extends readonly UseQueryResult<any, any>[],
> = {
  [K in keyof TQueries]: TQueries[K] extends UseQueryResult<infer Data, any>
    ? Data
    : never
}

interface MultiQueryPageLoaderProps<
  TQueries extends readonly UseQueryResult<any, any>[],
> {
  queries: TQueries
  children: (data: ExtractDataFromQueries<TQueries>) => React.ReactNode
  // Custom loading component (JSX)
  customLoader?: React.ReactNode
  // New prop to customize error display
  errorRenderer?: (error: any) => React.ReactNode
}

function MultiQueryPageLoader<
  TQueries extends readonly UseQueryResult<any, any>[],
>(props: MultiQueryPageLoaderProps<TQueries>) {
  const { queries, children, customLoader, errorRenderer } = props
  const { t: translate } = useTranslation()

  // Check if any query is loading
  const isLoading = queries.some((query) => query.status === 'pending')

  // Find the first error, if any
  const firstError = queries.find((query) => query.status === 'error')

  if (isLoading) {
    // Use custom loader if provided, otherwise fall back to default LoadingCard
    if (customLoader) {
      return <>{customLoader}</>
    }
    return <LoadingCard isLoading={true}>{translate('loading')}</LoadingCard>
  }

  if (firstError) {
    // Use custom error renderer if provided, otherwise fall back to default error component
    if (errorRenderer) {
      return <>{errorRenderer(firstError.error)}</>
    }
    return (
      <div className="max-w-xl mx-auto">
        <AppAlertComponent
          variant="error"
          title="Error"
          text={parseRequestError(firstError.error).message}
        >
          {null}
        </AppAlertComponent>
      </div>
    )
  }

  // Extract data from all queries with proper typing
  // Use a type assertion to correctly map the query results to their data types
  // We need to use as unknown as to preserve the tuple structure
  const data = queries.map(
    (query) => query.data
  ) as unknown as ExtractDataFromQueries<TQueries>

  return <>{children(data)}</>
}

export default MultiQueryPageLoader
